
import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator"
import { Getter, namespace } from "vuex-class"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import CaseTable from "@/components/CaseList/CaseTable.vue"
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import UserImage from "@/components/UserImage.vue"

import { get } from "lodash"
import moment from "moment"
import "@/scripts/array-operations"

import { CaseColumn, caseColumns } from "@/types/caseColumns"
import { CalculationType } from "@/types/field"

import { CalculationDataTypeValue, CaseDetails, DataType, dataTypeByFieldType, Field } from "@/types"
import { Team } from "@/types/team"
import { View } from "@/types/views/view"
import { Severity } from "@/types/severity"
import { groupingFunctions } from "@/types/dataType"
import { UserSettings } from "@/types/userSettings"
import { CurrentPageDetails } from "@/types/paginatedRequest"
import { Schedule } from "@/types/schedule"
import { CasesTable } from "@/types/case"
import { ViewsService } from "@/services/viewsService"

const caseStore = namespace("cases")
const setupStore = namespace("setup")

interface CaseListField {
	title: string,
	dataType: string | null,
	displayKey?: string | null,
	group?: (record: any, groupByValue: any) => { key: string, label: string, value: any }[],
	fieldDataType?: DataType | null
}

interface CaseListColumn {
	key: string,
	fieldId?: string,
	displayKey?: string,
	label: string,
	dataType?: string | null,
	fieldDataType?: DataType | null,
	rounding?: string,
	listLabel?: string,
	listItemType?: string,
	extensions?: {[key: string]: any}
	dynamicField?: boolean,
}

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"skeleton": SkeletonLoader,
		"case-table": CaseTable,
		"user-image": UserImage,
		"schedule-popup": () => import("@/components/Config/Schedules/ScheduleInstance.vue")
	}
})
export default class CaseList extends Vue {
	@Prop() private readonly activeView!: View | null;

	@Prop({ type: Number, default: 20 }) private readonly perPage!: number;
	@Prop({ type: String, default: "" }) private readonly searchFilter!: string;

	@Prop() private	casesTables!: CasesTable[] | null;

	@Prop({ type: String, default: "" })
	filter!: string;

	@Prop({ type: Number, default: 0 })
	totalRecords!: number

	@Prop({ default: null, required: false })
	groupValue!: string | null

	@caseStore.State private readonly severities!: Severity[];
	@caseStore.State viewsService!: ViewsService;

	@Getter("getScreenWidth") private readonly screenWidth!: number;
	@Getter("getTeamsById")	private readonly teamsById!: { [key: string]: Team };
	@Getter("getCaseName") private readonly getCaseName!: string;
	@Getter("getRecordName") private readonly getRecordName!: string;
	@Getter("getUserSettings") private readonly userSettings!: UserSettings;
	@Getter("getUseV2") private readonly shouldUseNewInterface!: boolean;

	@setupStore.State private readonly fields!: Field[];
	@setupStore.Action private readonly loadFieldsIfUnloaded!: () => Promise<void>;

	@setupStore.Getter("getFilterableFields") getFilterableFields!: CaseColumn[];

	private fieldsDictionary = new Map<string, CaseListField>();

	private defaultColumnSort: string = "";

	private viewScheduleInstance: {
		schedule: Schedule,
		date: Date
	} | null = null

	private groupPages: { [key: string]: CurrentPageDetails } = {};
	private debounceSearch: NodeJS.Timeout | null = null;

	private defaultSortDataType: DataType = DataType.none;
	private loadingPage: boolean = false;

	/* --- Vue Lifecycle --- */

	private async mounted (): Promise<void> {
		await this.loadFieldsIfUnloaded()
		this.buildFieldsDictionary()
		this.setupTablePages()
	}

	/* --- Computed Properties --- */

	public get columns (): (CaseListColumn | null)[] {
		if (!this.activeView) {
			return [
				{
					key: "caseNumber",
					label: "Case Number"
				},
				{
					key: "title",
					label: "Title"
				},
				{
					key: "templateTitle",
					label: "Template"
				},
				{
					key: "assigneeId",
					label: "Assignee",
					dataType: "user"
				}
			]
		}

		return this.activeView.columns.map(c => {
			if (c.startsWith("fields.")) {
				let fieldId = c.substring(7)

				if (fieldId.endsWith(".label")) {
					fieldId = fieldId.substring(0, fieldId.length - 6)
				}

				const fieldColumn = this.fields.find(field => field.id === fieldId)
				if (fieldColumn) {
					return {
						key: c,
						fieldId: fieldColumn.id,
						label: fieldColumn.title,
						dataType: fieldColumn.dataType,
						fieldDataType: fieldColumn.dataType === "calculated"
							? CalculationDataTypeValue[fieldColumn.extensions!.calculationType as CalculationType]
							: dataTypeByFieldType[fieldColumn.dataType!]
								? dataTypeByFieldType[fieldColumn.dataType!]
								: null,
						extensions: fieldColumn.extensions,
						dynamicField: true,
						listLabel: "label"
					}
				}
			} else {
				const caseColumn = this.getFilterableFields.find(column => column.key === c)
				if (caseColumn) {
					return {
						key: caseColumn.key,
						displayKey: caseColumn.displayKey,
						label: caseColumn.label,
						dataType: caseColumn.dataType,
						fieldDataType: caseColumn.fieldDataType,
						rounding: caseColumn.rounding,
						listLabel: caseColumn.listLabel || "title",
						listItemType: caseColumn.listItemType
					}
				}
			}

			return null
		}).filter(c => !!c)
	}

	public get checkRecordsAvailable (): boolean {
		return this.casesTables?.some((c: CasesTable) => c.totalRecords > 0) ?? false
	}

	private get groupedCases (): any[] {
		// Build fields dictionary for quick access using case columns and custom fields
		if (!this.loadingPage) {
			this.buildFieldsDictionary()
		}

		if (this.activeView && this.activeView.groupBy) {
			if (!this.fieldsDictionary.get(this.activeView.groupBy)) {
				this.activeView.groupBy = null
			}
		}

		if (this.activeView && this.activeView.groupBy) {
			const groupByKeyColumn = this.fieldsDictionary.get(this.activeView.groupBy)
			const groupBy = groupByKeyColumn && groupByKeyColumn.displayKey
				? groupByKeyColumn.displayKey
				: this.activeView!.groupBy

			const caseUsers: { [key:string]: any } = {}
			let getGroups: (record: any, groupByValue: any, cache: { [key:string]: any }, groupByOption?: string | null) => { key: string, label: string, value: any }[] =
				groupByKeyColumn && groupByKeyColumn.group
					? groupByKeyColumn.group
					: !!groupByKeyColumn && groupingFunctions.hasOwnProperty(groupByKeyColumn.fieldDataType ?? "")
						? get(groupingFunctions, groupByKeyColumn.fieldDataType ?? "")
						: (record: any, groupByValue: any, _cache, _groupByOption: string) => {
							return [
								{
									key: groupByValue || "not set",
									label: groupByValue || "Not Set",
									value: groupByValue
								}
							]
						}

			const groupLabels: { [key:string]: {
					label: string,
					value: any,
					sortValue?: any
				}
			} = {}

			if (this.casesTables && this.casesTables.length > 0) {
				this.casesTables.forEach((table: CasesTable) => {
					const caseItem = table.cases[0]
					let groupKey = get(caseItem, groupBy)
					const itemGroups = getGroups(table.cases[0], groupKey, caseUsers, this.activeView!.groupByOption)
					itemGroups.forEach(itemGroup => {
						if (this.activeView && this.activeView.groupByDataType === DataType.dateTime) {
							if (this.activeView.groupBy?.includes("fields")) {
								const label = table.value !== "none" ? table.value : "Not set"
								groupLabels[table.value] = {
									label,
									value: itemGroup.value
								}
							} else {
								groupKey = groupKey
									? moment(groupKey).format("L")
									: "Not set"
								groupLabels[table.value] = {
									label: groupKey,
									value: itemGroup.value
								}
							}
						} else if (this.activeView && this.activeView.groupByDataType === DataType.boolean) {
							groupLabels[table.value] = {
								label: table.value,
								value: table.value
							}
						} else {
							groupLabels[table.value] = {
								label: itemGroup.label,
								value: itemGroup.value
							}
						}
					})
				})
			}

			const res = this.casesTables?.map((table: CasesTable) => {
				return Vue.observable({
					key: table.value,
					label: groupLabels[table.value] ? groupLabels[table.value].label : table.value,
					value: groupLabels[table.value] ? groupLabels[table.value].value : table.value,
					sort: this.activeView!.groupBy === "severityId"
						? this.severityLevel(table.value)
						: groupLabels[table.value]
							? groupLabels[table.value].sortValue
								? groupLabels[table.value].sortValue
								: groupLabels[table.value].label
							: table.value,
					cases: table.cases,
					totalRecords: table.totalRecords,
					currentPage: this.groupPages[table.value]?.pageNumber ?? 1
				})
			})

			this.loadingPage = false
			return res ?? []
		} else {
			// We expect this to be a single table
			if (this.casesTables?.length === 1) {
				const table: CasesTable = this.casesTables[0]

				this.loadingPage = false
				return [Vue.observable({
					key: table.value,
					label: null,
					cases: table.cases,
					currentPage: this.groupPages[table.value]?.pageNumber ?? 1,
					value: table.value,
					sortBy: this.defaultColumnSort,
					sortDesc: false,
					totalRecords: table.totalRecords
				})]
			}

			this.loadingPage = false
			return []
		}
	}

	private get groupColumn (): CaseListField | undefined | null {
		if (this.activeView?.groupBy) {
			return this.fieldsDictionary.get(this.activeView?.groupBy)
		}

		return null
	}

	/* --- Watchers and Emit methods --- */
	@Watch("searchFilter")
	private updateBySearch (): void {
		if (this.debounceSearch) {
			clearTimeout(this.debounceSearch)
			this.debounceSearch = null
		}

		this.debounceSearch = setTimeout(() => {
			this.loadingPage = true
			Object.keys(this.groupPages).forEach(key => {
				if (!!this.groupPages && !!this.groupPages[key]) {
					this.groupPages[key].pageNumber = 1
					this.groupPages[key].sortBy = ""
					this.groupPages[key].sortDataType = DataType.none
					this.groupPages[key].sortDesc = false
				}
			})

			this.$emit("reloadInsightBySearch", this.searchFilter)
		}, 500)
	}

	@Watch("viewsService.activeViewId", { deep: true })
	private onActiveViewIdChanged (newValue: string, oldValue: string): void {
		if (newValue !== oldValue) {
			this.groupPages = {}
			if (!!this.casesTables && this.casesTables?.length > 0) {
				this.casesTables.forEach((table: CasesTable) => {
					this.groupPages[table.value] = {
						value: table.value,
						pageNumber: 1,
						sortBy: this.defaultColumnSort,
						sortDesc: false
					}
				})
			}
		}
	}

	@Emit("reloadInsightBySort")
	private setSortDetails (details: CurrentPageDetails): CurrentPageDetails {
		this.loadingPage = true

		const key = details.tableId ?? details.value

		this.groupPages[key] = {
			value: details.value,
			sortBy: details.sortBy,
			sortDesc: details.sortDesc,
			sortDataType: details.sortDataType,
			pageNumber: 1,
			tableId: details.tableId ? details.tableId : details.value
		}

		return {
			value: this.groupPages[key]?.value,
			pageNumber: this.groupPages[details.value]?.pageNumber ?? 1,
			pageSize: this.perPage,
			searchTerm: this.searchFilter,
			sortBy: this.groupPages[key]?.sortBy ?? this.defaultColumnSort,
			sortDesc: this.groupPages[key]?.sortDesc ?? false,
			sortDataType: this.groupPages[key]?.sortDataType,
			tableId: details.tableId ? details.tableId : details.value
		}
	}

	/* --- Methods --- */

	private scheduleUpdated (schedule: Schedule): void {
	}

	private onPageClick (group: any): void {
		if (!this.loadingPage) {
			this.loadingPage = true
			// Don't trigger a page change for the same page
			if (!!this.groupPages &&
					!!this.groupPages[group.key] &&
					!!this.groupPages[group.key].pageNumber &&
					this.groupPages[group.key].pageNumber === group.currentPage) {
				this.loadingPage = false
				return
			}

			this.groupPages[group.key] = {
				value: group.value,
				sortBy: this.groupPages[group.key]?.sortBy ?? this.defaultColumnSort,
				sortDesc: this.groupPages[group.key]?.sortDesc ?? false,
				sortDataType: this.groupPages[group.key]?.sortDataType ?? this.defaultSortDataType,
				pageNumber: group.currentPage ?? 1
			}

			const update: CurrentPageDetails = {
				value: group.value,
				pageNumber: group.currentPage,
				pageSize: this.perPage,
				searchTerm: this.searchFilter,
				sortBy: this.groupPages[group.key]?.sortBy,
				sortDesc: this.groupPages[group.key]?.sortDesc,
				sortDataType: this.groupPages[group.key]?.sortDataType,
				tableId: group.key ?? group.value
			}

			this.$emit("loadNextPage", update)
		}
	}

	private setupTablePages (): void {
		if (!!this.casesTables && this.casesTables?.length > 0) {
			this.groupPages = this.casesTables.reduce((acc, table) => {
				table.value = table.value ?? "none"

				acc[table.value] = {
					value: table.value,
					pageNumber: 1,
					sortBy: this.defaultColumnSort,
					sortDesc: false
				}
				return acc
			}, {} as { [key: string]: CurrentPageDetails })
		}
	}

	private buildFieldsDictionary (): void {
		// Build fields dictionary for quick access using case columns and custom fields
		caseColumns.forEach((field: CaseColumn) => {
			this.fieldsDictionary.set(field.key, { title: field.label, dataType: field.dataType, displayKey: field.displayKey, group: field.group })
		})

		this.fields.forEach((field: any) => {
			this.fieldsDictionary.set(`fields.${field.id}`, {
				title: field.title,
				dataType: field.dataType,
				displayKey: field.dataType === "user-role" ? null : "fields." + field.id + ".label",
				fieldDataType: field.dataType === "calculated"
					? CalculationDataTypeValue[field.extensions!.calculationType as CalculationType]
					: dataTypeByFieldType[field.dataType!]
						? dataTypeByFieldType[field.dataType!]
						: null
			})
		})
	}

	private severityLevel (severityTitle: string): number {
		const severity = this.severities && this.severities.find(p => p.title === severityTitle)
		if (severity) {
			return 9999 - severity.level
		} else {
			return 0
		}
	}

	// Method to display checkbox value as string dependent on its value
	private displayCheckBoxValue (value: string): string {
		switch (value.toLowerCase()) {
			case "null":
			case "false":
			case "no":
				return "No"
			case "undefined":
				return "Not Specified"
			default:
				return "Yes"
		}
	}

	private forecastClicked (caseItem: { scheduling: any }): void {
		if (!caseItem.scheduling) return

		this.viewScheduleInstance = {
			schedule: {
				id: caseItem.scheduling.scheduleId,
				title: caseItem.scheduling.scheduleTitle
			} as Schedule,
			date: new Date(caseItem.scheduling.instanceDate)
		}
	}

	private getFieldLabel (fieldId: string, caseDetails: CaseDetails): string | null {
		if (!caseDetails.fields || !caseDetails.fields[fieldId]) {
			return null
		}

		return caseDetails.fields[fieldId].label
	}

	private getSortColumn (key: string): string {
		return this.groupPages[key]?.sortBy ?? this.defaultColumnSort
	}

	private getSortDesc (key: string): boolean {
		return this.groupPages[key]?.sortDesc ?? false
	}
}
